import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Alert, Button, Card, ColorSchemeType, DataStatus} from 'platform/components';
import {Right, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery, useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';
import {VehicleWidget} from '@dms/teas';

import {RequiredTestIdProps, composePath, suffixTestId, useNavigate} from 'shared/web';

interface BusinessCaseTradeInCounterpartCardProps extends RequiredTestIdProps {
  linkedTradeInBusinessCaseId: string;
  // Host BC buyer's customer id. When provided and it differs from the counterpart BC's customer,
  // a warning alert is rendered — the same mismatch the link dialog warns about, surfaced as a
  // persistent state on the card. Kept optional so the card stays free of host-BC concerns.
  hostCustomerId?: string;
  // When provided, an "unlink trade-in" action is rendered (Trade-in tab only). The host BC id,
  // the unlink mutation and the read-only gating stay with the caller, so this card stays free of
  // host-BC and mutation concerns and can be reused read-only on the overview page.
  onUnlink?: () => void;
}

type CounterpartTypeFlag = {name: string; color: ColorSchemeType};

// Linked trade-in card: shows the counterpart BC's customer, its vehicle (standard VehicleWidget,
// same props as BusinessCasePurchaseVehicles) and a link to open the counterpart BC. Shared between
// the Trade-in tab (BusinessCaseTradeInLinked) and the Business Case overview page.
export function BusinessCaseTradeInCounterpartCard(props: BusinessCaseTradeInCounterpartCardProps) {
  const navigate = useNavigate();

  const {data: counterpart, isLoading: isLoadingCounterpart} = useGetBusinessCaseQuery({
    businessCaseId: props.linkedTradeInBusinessCaseId,
  });

  const customerId = counterpart?.customerId;
  const {data: customer, isLoading: isLoadingCustomer} = useGetCustomerV2Query(
    isNotNil(customerId) ? {customerId} : skipToken
  );

  const {vehicleId, linkedTitleKey, counterpartCustomerKey, counterpartRoute} = match(
    counterpart?.businessCaseType
  )
    .with('BUYING', () => ({
      vehicleId: counterpart?.offers?.[0]?.purchaseVehicles?.[0]?.vehicleId,
      linkedTitleKey: 'page.businessCase.tradeIn.linkedTitleBuying',
      counterpartCustomerKey: 'page.businessCase.tradeIn.counterpartCustomerBuying',
      counterpartRoute: businessCaseRoutes.overview,
    }))
    .with('SELLING', () => ({
      vehicleId: counterpart?.offers?.[0]?.saleVehicles?.[0]?.vehicleId,
      linkedTitleKey: 'page.businessCase.tradeIn.linkedTitleSelling',
      counterpartCustomerKey: 'page.businessCase.tradeIn.counterpartCustomerSelling',
      counterpartRoute: businessCaseRoutes.overview,
    }))
    .otherwise(() => ({
      vehicleId: undefined,
      linkedTitleKey: 'page.businessCase.tradeIn.linkedTitleBuying',
      counterpartCustomerKey: 'page.businessCase.tradeIn.counterpartCustomerBuying',
      counterpartRoute: businessCaseRoutes.overview,
    }));

  const typeFlag: CounterpartTypeFlag | undefined = match(counterpart?.businessCaseType)
    .with('SELLING', () => ({
      name: i18n.t('page.businessCase.labels.typeSellingWithSwap'),
      color: 'orange' as const,
    }))
    .with('BUYING', () => ({
      name: i18n.t('page.businessCase.labels.typeBuyingWithSwap'),
      color: 'green' as const,
    }))
    .otherwise(() => undefined);

  const {data: vehicle, isLoading: isLoadingVehicle} = useGetVehicleQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  const hasCustomerMismatch =
    isNotNil(props.hostCustomerId) &&
    isNotNil(counterpart?.customerId) &&
    counterpart?.customerId !== props.hostCustomerId;

  const handleOpenCounterpart = () => {
    navigate(
      composePath(counterpartRoute, {
        params: {id: props.linkedTradeInBusinessCaseId},
      })
    );
  };

  const cardActions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('page.businessCase.tradeIn.openCounterpart', {
        code: counterpart?.code ?? '',
      }),
      onClick: handleOpenCounterpart,
      'data-testid': suffixTestId('openCounterpart', props),
    },
  ];

  return (
    <DataStatus isLoading={isLoadingCounterpart || isLoadingCustomer || isLoadingVehicle}>
      <Card title={i18n.t(linkedTitleKey)} actions={cardActions}>
        <VStack spacing={4}>
          <Show when={hasCustomerMismatch}>
            <Alert
              type="inline"
              variant="warning"
              title={i18n.t('page.businessCase.tradeIn.customerMismatchAlert')}
              data-testid={suffixTestId('customerMismatch', props)}
            />
          </Show>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t(counterpartCustomerKey)}:
            </Text>
            <Text size="small" alternative>
              {getCustomerName(customer)}
            </Text>
          </VStack>
          <Show when={vehicle}>
            <VehicleWidget
              data-testid={suffixTestId('vehicle', props)}
              vehicle={vehicle}
              saleVehicleId={vehicleId ?? ''}
              shouldShowDetailButton={false}
              purchaseVehicle={counterpart?.offers?.[0]?.purchaseVehicles?.[0]}
              selling={counterpart?.offers?.[0]?.saleVehicles?.[0]}
              flags={typeFlag ? [typeFlag] : undefined}
            />
          </Show>
          <Show when={isNotNil(props.onUnlink)}>
            <Right>
              <Button
                variant="dangerOutlined"
                leftIcon="content/link_off"
                title={i18n.t('page.businessCase.tradeIn.unlink')}
                onClick={props.onUnlink}
                data-testid={suffixTestId('unlink', props)}
              />
            </Right>
          </Show>
        </VStack>
      </Card>
    </DataStatus>
  );
}
